// Core Modules
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

// External Modules
import Auth0Lock from 'auth0-lock';
import { BehaviorSubject, catchError, forkJoin, from, map, Observable, of, retry, Subject, Subscription, switchMap, tap, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

// Tokens
import { WINDOW } from '@core/injection-tokens/tokens';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { ConfigService } from '@core/services/config/config.service';
import { CryptoService } from '@core/services/crypto/crypto.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';
import { StorageTrackerService } from '@core/services/storage-tracker/storage-tracker.service';

// Models
import { AppEndpointsModel } from '@shared/models/endpoints.config.model';
import { IAuth0ClientInfo, IAuth0Profile, IUserProfileBasedOnFields, IUserProfileSessionDetails, IUserProfileWithClientList } from '@shared/models/auth0-types.model';

// Enums
import { LocalStorageEnum } from '@shared/enum/local-storage-keys.enum';
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';

// Environment
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // Injectors
  private readonly accessControlService = inject(AccessControlService);
  private readonly configService = inject(ConfigService);
  private readonly cookieService = inject(CookieService);
  private readonly cryptoService = inject(CryptoService);
  private readonly document = inject(DOCUMENT);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storageTrackerService = inject(StorageTrackerService);
  private readonly window = inject(WINDOW);

  // Private Members
  private readonly apiBaseUrl: string = this.configService.getAPIBaseUrl;
  private readonly appEndpoints: AppEndpointsModel = this.configService.getEndpointsByModule('appEndpoints') as AppEndpointsModel;
  private readonly authContents = this.configService.getContents('auth');
  private readonly isSessionValidSubject = new BehaviorSubject<boolean>(false);
  private readonly subscription = new Subscription();
  private lock?: Auth0LockStatic;
  private userEmail?: string;
  private userProfile: IAuth0Profile = JSON.parse(localStorage.getItem(LocalStorageEnum.USER_PROFILE) || '{}');
  private userprofileSessionDetails!: IUserProfileSessionDetails;

  // Public Members
  public authenticationInProgressSubject = new BehaviorSubject<boolean>(false);
  public clientList: IAuth0ClientInfo[] = [];
  public clientNavigationInProgressSubject = new BehaviorSubject<boolean>(false);
  public readonly isAuthenticationInProgress$ = this.authenticationInProgressSubject.asObservable();
  public readonly isClientNavigationInProgress$ = this.clientNavigationInProgressSubject.asObservable();
  public readonly isSessionValid$ = this.isSessionValidSubject.asObservable();
  public logoutSubject$: Subject<boolean> = new Subject<boolean>();
  public visibilityListener?: () => void;

  // Public Getters
  public get getAuthToken(): string {
    this.userProfile = this.getUserProfile();
    return this.userProfile?.hgApiToken ? this.userProfile.hgApiToken.trim() : '';
  }

  /**
   * Clearing Session
   * @memberof AuthService
   */
  private clearSession(): void {
    localStorage.clear();
    this.cookieService.deleteAll('/');
    sessionStorage.clear();
  }

  /**
   * This method is used for fetching the Auth0 user info by using Auth0 lock getUserInfo()
   * Auth0 user information is stored in local storage.
   * @param {AuthResult} authResult
   * @memberof AuthService
   */
  public fetchUserProfileInfo(accessToken: string, lock?: boolean): Promise<IAuth0Profile> {
    if (lock) {
      const userDomain: string = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN) as string;
      this.lock = this.initializeLock(userDomain, {
        auth: {
          redirectUrl: `${this.window.location.origin}/login`,
          responseType: 'token',
          params: {
            scope: 'openid',
            tenant: environment.AUTH0.tenant,
          },
        },
      });
    }
    console.log('<AuthService> - <fetchUserProfileInfo> Get User Info...');
    return new Promise((resolve, reject) => {
      this.lock?.getUserInfo(accessToken, (error: auth0.Auth0Error, profile: auth0.Auth0UserProfile) => {
        if (error) {
          console.error('<AuthService> - <fetchUserProfileInfo> Auth service on user authenticated :: getUserInfo :: ERROR', error);
          reject(error);
        } else {
          resolve(profile as IAuth0Profile);
        }
      });
    });
  }

  /**
   * This method is used to get default Auth0 Popup Configurations
   *
   * @private
   * @param {string} email
   * @return {*}  {Auth0LockConstructorOptions}
   * @memberof AuthService
   */
  private getAuth0DefaultOptions(): Auth0LockConstructorOptions {
    return {
      container: 'authLoginContainer', // id for the html container
      avatar: null,
      auth: {
        redirect: true,
        responseType: 'token',
        params: {
          scope: 'openid',
          tenant: this.configService.auth0Tenant,
        },
        autoParseHash: false,
      },
      theme: {
        primaryColor: '#68BDCF', // Button color
        logo: '',
      },
      allowSignUp: false,
      allowForgotPassword: true,
      languageDictionary: {
        title: '', // Remove title from header
      },
      prefill: {
        email: this.userEmail,
        username: this.userEmail,
      },
      configurationBaseUrl: 'https://cdn.auth0.com',
      rememberLastLogin: false,
    };
  }

  /**
   * This method is used for initializing new Auth0Lock
   * @param {string} domain Domain will change for regular and custom
   * @param {string} options
   * @memberof AuthService
   */
  private initializeLock(domain: string, options: Auth0LockConstructorOptions = {}): Auth0LockStatic {
    return new Auth0Lock(environment.AUTH0.clientId, domain, options);
  }

  /**
   *
   * This method is used for updating the User Profile information with encrypted format in Local Storage.
   * @param {IAuth0Profile} metaUserInfo
   * @memberof AuthService
   */
  private saveUserProfileToLocalStorage(metaUserInfo: IAuth0Profile): void {
    try {
      const userProfile: Record<string, any> = {
        email: metaUserInfo.email ? this.cryptoService.encrypt(metaUserInfo.email) : '',
        picture: metaUserInfo.picture,
        nickname: metaUserInfo.nickname ? this.cryptoService.encrypt(metaUserInfo.nickname) : '',
        name: metaUserInfo.name ? this.cryptoService.encrypt(metaUserInfo.name) : '',
        hgApiToken: metaUserInfo.hgApiToken,
        hgApiTokenId: metaUserInfo.hgApiTokenId,
        hgCredential: {
          user_email: metaUserInfo.hgCredential.user_email ? this.cryptoService.encrypt(metaUserInfo.hgCredential.user_email) : '',
        },
        clientID: this.cryptoService.encrypt(metaUserInfo.clientID),
      };
      if (metaUserInfo?.identities?.length > 0 && metaUserInfo?.identities[0].connection) {
        localStorage.setItem(LocalStorageEnum.CONNECTION, this.cryptoService.encrypt(metaUserInfo?.identities[0].connection));
      }
      localStorage.setItem(LocalStorageEnum.USER_PROFILE, JSON.stringify(userProfile));
      const now = new Date();
      const plus15Min = new Date(now.getTime() + 15 * 60 * 1000);
      localStorage.setItem(LocalStorageEnum.EXPIRES_AT, this.cryptoService.encrypt(plus15Min.toISOString()));
    } catch (error) {
      console.log('<AuthService> - <saveUserProfileToLocalStorage> Error occurred while persisting User profile information in local storage...', error);
      throw error;
    }
  }

  private tokenRotationRequired(): boolean {
    const now = new Date();
    const expiresAt = localStorage.getItem(LocalStorageEnum.EXPIRES_AT) as string;
    const tokenExpireAtInUTC = new Date(this.cryptoService.decrypt(expiresAt));
    const tokenExpireTime = tokenExpireAtInUTC.getTime();
    // if expiresAt is not set or invalid, we assume token rotation is required
    if (!expiresAt || isNaN(tokenExpireTime)) return true;
    const diffMs = Math.abs(Math.round(tokenExpireTime - now.getTime()));
    console.log(`<AuthService> - <tokenRotationRequired> - local storage expires at: ${tokenExpireAtInUTC}, duration: ${Math.round(diffMs / 60000)} mins left before the token expires.`);
    return diffMs < 600000; // 10 minutes in milliseconds
  }

  /**
   * This method will return clientId from Auth0 User Profile or an empty string.
   * If hg-customer property exists in Auth0 User Profile then return hg-customer.clientId.
   * If hg-customer property not exists but Auth0 User Profile exists in Auth0 User Profile
   * then return userProfile.clientID.
   * If Auth0 User Profile not exists then return empty string.
   * @return {*}
   * @memberof AuthService
   */
  public getClientId(): string {
    const userProfile: IUserProfileBasedOnFields = this.getUserProfileBasedOnFields([UserProfileFieldsEnum.CLIENT_ID, UserProfileFieldsEnum.HG_CUSTOMER]);
    let clientId: string = '';
    if (userProfile[UserProfileFieldsEnum.HG_CUSTOMER]?.clientId) {
      clientId = userProfile[UserProfileFieldsEnum.HG_CUSTOMER].clientId.toString();
    } else if (userProfile.clientID) {
      clientId = userProfile.clientID;
    }
    return clientId;
  }

  /**
   * Utility method to get the customer id
   * @returns Customer Id of logged in user else empty string
   */
  public getCustomerId(): string {
    const userProfile: IUserProfileBasedOnFields = this.getUserProfileBasedOnFields([UserProfileFieldsEnum.HG_CUSTOMER]);
    let clientId = '';
    if (userProfile[UserProfileFieldsEnum.HG_CUSTOMER] && userProfile[UserProfileFieldsEnum.HG_CUSTOMER]?.clientId) {
      clientId = userProfile[UserProfileFieldsEnum.HG_CUSTOMER].clientId.toString().trim();
    }
    return clientId;
  }

  /**
   * This method is used to obtain for legacy federated domains.
   *
   * @return {Observable<string[] | Error>}
   * @memberof AuthService
   */
  public getLegacyFederatedDomains(): Observable<Array<string>> {
    return this.httpClient.get<string[]>(`${this.configService.getAPIBaseUrl}${this.appEndpoints.legacyFederatedDomain}`);
  }

  /**
   * This method will return Auth0 User Profile which is stored in Local storage.
   *
   * @return {object} auth0.Auth0UserProfile or undefined
   * @memberof AuthService
   */
  public getUserProfile(): IAuth0Profile {
    return JSON.parse(localStorage.getItem(LocalStorageEnum.USER_PROFILE) || '{}');
  }

  /**
   * This method will return Auth0 User Profile which is stored in Local storage.
   *
   * @return {object} auth0.Auth0UserProfile or undefined
   * @memberof AuthService
   */
  public getUserProfileBasedOnFields(userProfileFields: string[]): IUserProfileBasedOnFields {
    const metaUserInfo: IUserProfileBasedOnFields = JSON.parse(localStorage.getItem(LocalStorageEnum.USER_PROFILE) || '{}');
    if (!Object.keys(metaUserInfo).length) return metaUserInfo;
    let userInfo: IUserProfileBasedOnFields = {
      email: userProfileFields.includes(UserProfileFieldsEnum.EMAIL) ? this.cryptoService.decrypt(metaUserInfo.email) : '',
      name: userProfileFields.includes(UserProfileFieldsEnum.NAME) ? this.cryptoService.decrypt(metaUserInfo.name) : '',
      clientID: userProfileFields.includes(UserProfileFieldsEnum.CLIENT_ID) ? this.cryptoService.decrypt(metaUserInfo.clientID) : '',
      [UserProfileFieldsEnum.HG_CUSTOMER]: {
        clientId: '',
        clientName: '',
        clientCode: '',
      },
    };
    if (metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER] && userProfileFields.includes(UserProfileFieldsEnum.HG_CUSTOMER)) {
      userInfo = {
        ...userInfo,
        [UserProfileFieldsEnum.HG_CUSTOMER]: {
          clientId: metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER].clientId ? this.cryptoService.decrypt(metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER].clientId.toString()) : '',
          clientName: metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER].clientName ? this.cryptoService.decrypt(metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER].clientName) : '',
          clientCode: metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER].clientCode ? this.cryptoService.decrypt(metaUserInfo[UserProfileFieldsEnum.HG_CUSTOMER].clientCode) : '',
        },
      };
    }
    return userInfo;
  }

  /**
   * This method is used for initializing Auth0 locked widget
   * based on the domain name.
   * For Ignite user => Legacy federated Auth0 domain
   * For User name & Password Authentication => Custom domain
   * @param {string} domain (either legacy federated domain or custom domain)
   * @param {string} email
   * @memberof AuthService
   */
  public initializeLockWidget(email: string, domain: string = environment.AUTH0.domain): void {
    localStorage.setItem(LocalStorageEnum.AUTH0_DOMAIN, domain || environment.AUTH0.domain);
    this.userEmail = email;
    this.lock = this.initializeLock(domain, this.getAuth0DefaultOptions());
    this.lock.show();
  }

  /**
   * This method is called once user reached idle timeout and every 10 Mins to acquire new HG-API Token
   *
   * @return {*}
   * @memberof AuthService
   */
  public invokeRefreshToken(): Observable<IUserProfileSessionDetails> {
    console.log('<AuthService> - <invokeRefreshToken> Invoking refresh token ...');
    const userProfile = JSON.parse(localStorage.getItem(LocalStorageEnum.USER_PROFILE) || '{}');
    const userDomain = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN) as string;

    if (!userProfile?.hgApiTokenId || !userDomain) {
      return throwError(() => new Error('USER_DOMAIN_NOT_EXISTS'));
    }

    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${userProfile.hgApiToken}`,
    });
    return this.httpClient.post<IUserProfileSessionDetails>(`${this.apiBaseUrl}${this.appEndpoints.sessionRefresh}`, { tokenId: userProfile.hgApiTokenId }, { headers: httpHeaders }).pipe(
      map((userProfileSessionDetails: IUserProfileSessionDetails) => {
        console.log(`<AuthService> - <invokeRefreshToken> Invoked Refresh Token Successfully at ${new Date()}, expires at : ${userProfileSessionDetails?.expires}`);
        localStorage.setItem(LocalStorageEnum.EXPIRES_AT, this.cryptoService.encrypt(userProfileSessionDetails?.expires));
        this.userprofileSessionDetails = userProfileSessionDetails;
        return userProfileSessionDetails;
      }),
      catchError(this.errorHandlerService.errorHandler)
    );
  }

  /**
   * This method is used for checking whether session is valid or not
   * Calculation :
   *   1: User Profile, Auth0 Domain should be exists.
   *   2: EXPIRES_AT value should be greater than current epoch Date time.
   * @return {Observable} isSessionValidSubject
   * @memberof AuthService
   */
  public isSessionValid(): Observable<boolean> {
    const now = new Date();
    const userProfile = JSON.parse(localStorage.getItem(LocalStorageEnum.USER_PROFILE) as string);
    const userDomain = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN) as string;
    const expiresAt = localStorage.getItem(LocalStorageEnum.EXPIRES_AT);
    const tokenExpiredAt = expiresAt ? this.cryptoService.decrypt(expiresAt) : false;
    let isValidToken = false;
    if (tokenExpiredAt && isNaN(Date.parse(tokenExpiredAt)) === false) {
      const tokenExpiredValue = new Date(tokenExpiredAt).getTime();
      const diffMs = Math.round(tokenExpiredValue - now.getTime());
      isValidToken = Math.abs(diffMs / 60000) > 0;
      console.log(`<AuthService> - <isSessionValid> - ${Math.abs(diffMs / 60000)} mins left before the token expires...`);
    }
    this.isSessionValidSubject.next(userProfile && userDomain && isValidToken ? true : false);
    return this.isSessionValid$;
  }

  /**
   * loginWithRedirect : This method is called by AuthGuard once waitForHandleAuthCallbackToComplete is completed
   * 1. Update User Profile info in Local Storage
   * @param {IAuth0Profile} metaUserInfo
   * @memberof AuthService
   */
  public loginWithRedirect(userProfileWithClientDetails: IUserProfileWithClientList): Observable<any> {
    try {
      if (userProfileWithClientDetails.userProfile) {
        this.saveUserProfileToLocalStorage(userProfileWithClientDetails.userProfile);
        // initialize gain insights
        return this.accessControlService.definePermissions().pipe(
          switchMap(() => {
            return this.accessControlService.getLandingPageBasedOnUserScopes().then((landing: string) => landing);
          })
        );
      } else {
        this.authenticationInProgressSubject.next(false);
        return of(`/${MainNavigationEnum.LOGIN}`);
      }
    } catch (error) {
      console.log('<AuthService> - <loginWithRedirect> Caught exception in login with Redirect method...', error);
      this.authenticationInProgressSubject.next(false);
      return of(`/${MainNavigationEnum.LOGIN}`);
    }
  }

  /**
   * This method is used for logging out from the Auth0 session
   * After logout from Auth0 session is done clear out cookies and local storage
   * @memberof AuthService
   */
  public logout(): void {
    this.storageTrackerService.stop();
    this.clearSession();
    this.logoutSubject$.next(true);
    this.logoutSubject$.complete();
    if (this.visibilityListener) {
      console.log('<AuthService> Unregistering visibility listener from the document...');
      this.document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = undefined;
    }
    // Set to empty string if hash is present in URL bar before logout
    this.router.navigate([`/${MainNavigationEnum.LOGIN}`], {
      onSameUrlNavigation: 'reload',
    });
  }

  /**
   * This method is used to get New Auth0 token for an authenticate user.
   * Get a new token from Auth0 for an authenticated user.
   * The poll interval between checks to checkSession() should be at least 15 minutes
   * between calls to avoid any issues in the future with rate limiting of this call.
   * @memberof AuthService
   */
  public refreshSession(isBrowserRefresh = false): Observable<any> {
    console.log('<AuthService> - <refreshSession()> - Refreshing session...');
    const userDomain: string = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN) as string;
    if (!userDomain) return throwError(() => new Error('USER_DOMAIN_NOT_EXISTS'));
    const authParamsOptions: Auth0LockAuthParamsOptions = {};
    const auth0LockConstructorOptions: Auth0LockConstructorOptions = {
      auth: {
        redirectUrl: `${this.window.location.protocol}//${this.window.location.host}/login`,
        responseType: 'token',
        params: {
          scope: 'openid',
          tenant: this.configService.auth0Tenant,
        },
        sso: false,
      },
    };
    // Wrapping checkSession in an Observable with retry
    const auth0CheckSessionPromise = new Observable<AuthResult>((observer) => {
      this.lock = this.initializeLock(userDomain, auth0LockConstructorOptions);
      this.lock.checkSession(authParamsOptions, (err: auth0.Auth0Error, authResult: AuthResult | undefined) => {
        if (err || !authResult) {
          console.log(`<AuthService> - <refreshSession> Error while auth0 checkSession at ${new Date()} : ${JSON.stringify(err)}`);
          observer.error(err); // ← this triggers a retry // Emit an error if checkSession fails
        } else {
          console.log(`<AuthService> - <refreshSession> auth0 checkSession at ${new Date()} : Successful`);
          observer.next(authResult); // Emit the result if checkSession succeeds
          observer.complete(); // Complete the observable
        }
      });
    }).pipe(
      retry(this.authContents.retryCount), // Retry up to 1 times before failing
      catchError((error) => {
        console.error('<AuthService> - <refreshSession> Failed after retries:', error);
        return throwError(() => error); // Propagate the error
      })
    );
    // When AuthGuard is invoked and it's not a browser refresh scenario, we need to check if token rotation is required.
    return this.tokenRotationRequired() || isBrowserRefresh
      ? forkJoin({
          authResult: auth0CheckSessionPromise,
          session: this.invokeRefreshToken(),
        })
      : of({
          authResult: {} as AuthResult,
          session: this.userprofileSessionDetails,
        });
  }

  /**
   * This function is called in case of browser refresh / route change / login navigation start.
   * Fetch the access_token/id_token from url and Auth0 domain from local storage.
   * Use to complete authentication flow when autoParseHash is false.
   * On success in_token and user_profile is set to local storage.
   * @memberof AuthService
   */
  public resumeAuth(): Observable<IUserProfileWithClientList> {
    const userDomain: string = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN) as string;
    const hash: string = this.window.location.hash;
    const auth0SuccessCallbackPromise: Promise<IAuth0Profile> = new Promise((resolve, reject) => {
      /* istanbul ignore next */
      this.lock = this.initializeLock(userDomain, {
        rememberLastLogin: false,
        auth: {
          sso: false,
        },
      });
      this.lock?.resumeAuth(hash, (error: auth0.Auth0Error, authResult: AuthResult) => {
        if (error) reject(error);
        else resolve(this.fetchUserProfileInfo(authResult?.accessToken));
      });
    });
    return from(auth0SuccessCallbackPromise).pipe(
      tap(() => this.authenticationInProgressSubject.next(true)),
      switchMap((userProfile: IAuth0Profile) => {
        return forkJoin({
          userProfile: Promise.resolve(userProfile),
          clientDetails: Promise.resolve(null),
        });
      }),
      catchError((e) => {
        console.log('<AuthService> - <resumeAuth> Error occurred while fetching user profile & client list details', e);
        this.authenticationInProgressSubject.next(false);
        return of({ userProfile: null, clientDetails: null });
      })
    );
  }

  /**
   *
   * This method is used to update client info along with user profile information in Local Storage.
   * @param {IAuth0ClientInfo} clientInfo
   * @memberof AuthService
   */
  public updateClientInfo(clientInfo: IAuth0ClientInfo): void {
    const userProfile = this.getUserProfile();
    localStorage.setItem(
      LocalStorageEnum.USER_PROFILE,
      JSON.stringify({
        ...userProfile,
        ...{
          [UserProfileFieldsEnum.HG_CUSTOMER]: {
            clientId: this.cryptoService.encrypt(clientInfo.clientId.toString()),
            clientName: this.cryptoService.encrypt(clientInfo.clientName),
            clientCode: this.cryptoService.encrypt(clientInfo.clientCode),
          },
        },
      })
    );
  }

  /**
   * method to destroy Subscription
   */
  public ngOnDestroy(): void {
    console.log('<AuthService> - <ngOnDestroy> Destroying AuthService...');
    this.subscription.unsubscribe();
  }
}
